import { useGetPerformProgressQuery } from '@/services/organizations';
import {
  Badge,
  BadgeVariant,
  Box,
  Button,
  Details,
  DimensionSize,
  toast,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AdderType,
  AdditionalCostAdder,
  AdditionalCostAdderType,
  BatteryPricingMethodOption,
  ConstructionAdderType,
  ConsumerSolarQuoteType,
  ProgramType,
  Quote,
  QuoteStatus,
  UserPermissions,
} from 'types';

import { getAdderTypeLabel } from '../../helpers/addersHelpers';
import { currencyFormatter, kwhPriceFormatter } from '../../helpers/currencyFormatter';
import { useSendContractV2Mutation } from '../../services/contract';
import { isErrorWithData } from '../../services/helpers';
import { useVoidQuoteMutation } from '../../services/quotes';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import RequirePermissions from '../auth/requirePermissions';

import {
  getEpcRatePPWLabel,
  getEpcTotalPaymentLabel,
  ListItemElement,
  ListItemElementPropsWithTooltip,
  ListItemElementWithMultipleProps,
  useGetDomesticElements,
} from './QuoteListItem.utils';

const getAdderLabel = (adder: AdderType, batteryDetails: any): string => {
  if (adder === AdderType.backupBattery && batteryDetails?.pricingMethod === 'kwhRate') {
    return getAdderTypeLabel(AdderType.backupBatteryKwh) ?? adder;
  }
  if (adder === AdderType.arbitrageBattery && batteryDetails?.pricingMethod === 'rawCost') {
    return getAdderTypeLabel(AdderType.arbitrageBatteryCost) ?? adder;
  }

  return getAdderTypeLabel(adder) ?? adder;
};

interface QuoteListItemProps {
  item: Quote;
  showCreateChangeOrderButton?: boolean;
  indentLeft?: number;
}

const CustomerFacingSection = ({
  item,
  isHVACWaterHeaterQuote,
  hasArbitrageBattery,
  hasCostBasedBattery,
  hasBackupBatteryKWH,
  systemPricingDetails,
  hasSolarReadiness,
  solarReadinessCost,
  totalAmountPaid,
  valueStyle,
}: {
  item: Quote;
  isHVACWaterHeaterQuote: boolean;
  hasArbitrageBattery: boolean;
  hasCostBasedBattery: boolean;
  hasBackupBatteryKWH: boolean;
  systemPricingDetails: any;
  hasSolarReadiness: boolean;
  solarReadinessCost: number;
  totalAmountPaid: number;
  valueStyle?: React.CSSProperties;
}) => (
  <>
    <Box direction="row" padding="sm lg" gap="5xl">
      <Box width={{ base: 'xs' as DimensionSize, tablet: 'lg' }} />
      <Box as="h3" fontWeight="medium" fontSize="md">
        Customer Facing
      </Box>
    </Box>

    {!isHVACWaterHeaterQuote && (
      <ListItemElement
        label={
          hasArbitrageBattery
            ? 'Solar with Arbitrage Battery Rate ($/kWh)'
            : hasBackupBatteryKWH
              ? 'Solar with Backup Battery Rate ($/kWh)'
              : 'Solar-Only Rate ($/kWh)'
        }
        value={kwhPriceFormatter.format(item.kwhPrice)}
        valueStyle={valueStyle}
      />
    )}
    <ListItemElement
      label={
        hasArbitrageBattery
          ? 'Estimated Solar with Arbitrage Battery Year-1 Monthly Payment'
          : hasBackupBatteryKWH
            ? 'Estimated Solar with Backup Battery Year-1 Monthly Payment'
            : !isHVACWaterHeaterQuote
              ? 'Estimated Solar-Only Year-1 Monthly Payment'
              : 'Estimated Year-1 Monthly Payment'
      }
      value={currencyFormatter.format(systemPricingDetails[0].monthlyPayment)}
      valueStyle={valueStyle}
    />
    {hasCostBasedBattery && (
      <>
        <ListItemElement
          label="Estimated Battery Year-1 Monthly Payment"
          value={currencyFormatter.format(systemPricingDetails[0].batteryPayment || 0)}
          valueStyle={valueStyle}
        />
      </>
    )}
    <ListItemElement
      label={'Total Year-1 Monthly Payment'}
      value={currencyFormatter.format(systemPricingDetails[0].totalMonthlyPayment)}
      valueStyle={valueStyle}
    />
    {hasSolarReadiness && (
      <ListItemElement
        label="Solar Readiness"
        value={currencyFormatter.format(solarReadinessCost)}
        valueStyle={valueStyle}
      />
    )}
    <ListItemElement
      label={!isHVACWaterHeaterQuote ? 'Total Lifetime Solar Lease Payments' : 'Total Lease Monthly Payments'}
      value={currencyFormatter.format(totalAmountPaid)}
      valueStyle={valueStyle}
    />
  </>
);

const EPCFacingSection = ({
  isHVACWaterHeaterQuote,
  epcRatePPW,
  epcTotalPaymentLabel,
  epcRatePPWDomesticContentElements,
  epcTotalPaymentDomesticContentElements,
  totalSystemCost,
  hasCostBasedBattery,
  totalBatteryCost,
  totalEPCPaymentElements,
  pricePerWatt,
  totalAdderCost,
  effectivePPWElements,
  effectivePPWRate,
  valueStyle,
}: {
  isHVACWaterHeaterQuote: boolean;
  epcRatePPW: string;
  epcTotalPaymentLabel: string;
  epcRatePPWDomesticContentElements: ListItemElementPropsWithTooltip[];
  epcTotalPaymentDomesticContentElements: ListItemElementPropsWithTooltip[];
  totalSystemCost: number;
  hasCostBasedBattery: boolean;
  totalBatteryCost: number | undefined;
  totalEPCPaymentElements: ListItemElementPropsWithTooltip[];
  pricePerWatt: number;
  totalAdderCost: number;
  effectivePPWElements: ListItemElementPropsWithTooltip[];
  effectivePPWRate?: number;
  valueStyle?: React.CSSProperties;
}) => (
  <>
    <Box direction="row" padding="lg lg sm" gap="5xl">
      <Box width={{ base: 'xs' as DimensionSize, tablet: 'lg' }} />
      <Box as="h3" fontWeight="medium" fontSize="md">
        EPC Facing
      </Box>
    </Box>

    {!isHVACWaterHeaterQuote &&
      (epcRatePPWDomesticContentElements.length ? (
        <ListItemElementWithMultipleProps baseLabel={epcRatePPW} values={epcRatePPWDomesticContentElements} />
      ) : (
        <ListItemElement
          label={epcRatePPW}
          value={pricePerWatt ? currencyFormatter.format(pricePerWatt) : 'N/A'}
          valueStyle={valueStyle}
        />
      ))}
    {!isHVACWaterHeaterQuote && effectivePPWElements.length ? (
      <ListItemElementWithMultipleProps baseLabel={'Effective EPC Rate ($/W)'} values={effectivePPWElements} />
    ) : hasCostBasedBattery && effectivePPWRate ? (
      <ListItemElement
        label={'Effective EPC Rate ($/W)'}
        value={effectivePPWRate ? currencyFormatter.format(effectivePPWRate) : 'N/A'}
        valueStyle={valueStyle}
      />
    ) : null}
    {epcTotalPaymentDomesticContentElements.length ? (
      <ListItemElementWithMultipleProps
        baseLabel={epcTotalPaymentLabel}
        values={epcTotalPaymentDomesticContentElements}
      />
    ) : (
      <ListItemElement
        label={epcTotalPaymentLabel}
        value={totalSystemCost ? currencyFormatter.format(totalSystemCost - totalAdderCost) : 'N/A'}
        valueStyle={valueStyle}
      />
    )}
    {hasCostBasedBattery && totalBatteryCost ? (
      <ListItemElement
        label="Battery EPC Payment"
        value={totalBatteryCost ? currencyFormatter.format(totalBatteryCost) : 'N/A'}
        valueStyle={valueStyle}
      />
    ) : null}
    {hasCostBasedBattery &&
      (totalEPCPaymentElements.length ? (
        <ListItemElementWithMultipleProps baseLabel={'Total EPC Payment'} values={totalEPCPaymentElements} />
      ) : (
        <ListItemElement
          label="Total EPC Payment"
          value={totalSystemCost ? currencyFormatter.format(totalSystemCost) : 'N/A'}
          valueStyle={valueStyle}
        />
      ))}
  </>
);

export function QuoteListItem({
  item,
  showCreateChangeOrderButton,
  indentLeft,
}: QuoteListItemProps): React.JSX.Element {
  const {
    totalAmountPaid,
    kwhPrice,
    systemPricingDetails,
    totalSystemCost,
    pricePerWatt,
    productName,
    status,
    adders,
    additionalCostAdders,
    batteryDetails,
    programType,
    quoteType,
    systemSizeKw,
    systemFirstYearProductionKwh,
  } = item as any; // TODO: This will be updated once we move to v3 routes. This is to support legacy backup battery data structure
  const showVoidButton = status === QuoteStatus.active || status === QuoteStatus.contracted;
  // A superseded (amended) quote's figures are struck through so the active quote reads as current.
  const struckStyle = status === QuoteStatus.amended ? { textDecoration: 'line-through' as const } : undefined;
  const [voidQuote, { isLoading: isVoidingQuote }] = useVoidQuoteMutation({
    fixedCacheKey: 'useVoidQuoteMutationKey',
  });
  const { data: isAccountEditEnabled } = useGetPerformProgressQuery(item.accountId);
  const [_sendContract, { isLoading: isSendingContract }] = useSendContractV2Mutation({
    fixedCacheKey: 'useSendContractV2MutationKey',
  });
  const { isOpen: isDetailsOpen, handleToggle: handleDetailsToggle } = useOpenClose({
    defaultIsOpen: showVoidButton,
  });
  const quoteStatus: {
    [key in QuoteStatus]: {
      label: string;
      variant: BadgeVariant;
    };
  } = {
    [QuoteStatus.contracted]: {
      label: 'Contracted',
      variant: 'success',
    },
    [QuoteStatus.active]: {
      label: 'Active',
      variant: 'success',
    },
    [QuoteStatus.voided]: {
      label: 'Voided',
      variant: 'warning',
    },
    [QuoteStatus.preBillingCustomerAdjustment]: {
      label: 'Pre-Billing Customer Adjustment',
      variant: 'info',
    },
    [QuoteStatus.amended]: {
      label: 'Amended',
      variant: 'info',
    },
    [QuoteStatus.changeOrderPending]: {
      label: 'Change Order Pending',
      variant: 'info',
    },
  };
  const getAdderList = (adders?: string[]) => {
    return adders ? adders.map((adder) => getAdderLabel(adder as AdderType, batteryDetails)).join(', ') : '';
  };
  const handleVoidQuote = async () => {
    const toastId = toast.loading('Voiding quote');

    try {
      const confirmed = window.confirm('Are you sure you want to Void this Quote? This action cannot be undone.');

      if (!confirmed) return;
      await voidQuote({ accountId: item.accountId, quoteId: item.id }).unwrap();
      toast.success('Quote voided successfully');
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(errorMessage);
      } else {
        console.error(e);
        toast.error('Error voiding quote');
      }
    } finally {
      toast.dismiss(toastId);
    }
  };
  const allowsAction = useIsOrgReadOnly();
  const navigate = useNavigate();
  const numberFormatter = useMemo(() => new Intl.NumberFormat('en-US'), []);

  const hasCostBasedBattery = adders
    ? (adders.includes(AdderType.backupBattery) || adders?.includes(AdderType.arbitrageBattery)) &&
      batteryDetails?.pricingMethod === BatteryPricingMethodOption.rawCost
    : false;
  const hasBackupBatteryKWH = adders
    ? adders.includes(AdderType.backupBattery) &&
      batteryDetails?.pricingMethod === BatteryPricingMethodOption.kwhRate
    : false;
  const hasSolarReadiness = adders ? adders.includes(ConstructionAdderType.solarReadiness) : false;
  const hasArbitrageBattery =
    (adders?.includes(AdderType.arbitrageBattery) &&
      batteryDetails?.pricingMethod === BatteryPricingMethodOption.kwhRate) ||
    false;
  const isHVACWaterHeaterQuote =
    programType === ProgramType.hvac ||
    programType === ProgramType.waterHeater ||
    programType === ProgramType.generator;
  const isEnergyBackupQuote = programType === ProgramType.energyBackup;
  const totalBackupBatteryCost = additionalCostAdders?.find(
    (adder: any) => adder.type === AdderType.backupBattery,
  )?.cost;
  const totalArbitrageBatteryCost = additionalCostAdders?.find(
    (adder: any) => adder.type === AdderType.arbitrageBattery,
  )?.cost;
  const totalBatteryCost = totalBackupBatteryCost || totalArbitrageBatteryCost || 0;
  const totalAdderCost =
    additionalCostAdders?.reduce((acc: number, adder: any) => {
      acc += adder.cost;
      return acc;
    }, 0) ?? 0;
  const solarReadinessCost = hasSolarReadiness
    ? additionalCostAdders.find(
        (element: AdditionalCostAdder) => element.type === AdditionalCostAdderType.solarReadiness,
      )?.cost || 0
    : 0;
  let title = productName;
  if (!isHVACWaterHeaterQuote && !isEnergyBackupQuote) {
    title += `, ${kwhPriceFormatter.format(kwhPrice)}/kWh, ${systemSizeKw} kW, ${numberFormatter.format(systemFirstYearProductionKwh)} kWh`;
  }
  const epcTotalPaymentLabel = getEpcTotalPaymentLabel({
    hasArbitrageBattery,
    hasBackupBatteryKWH,
    isHVACWaterHeaterQuote,
  });
  const epcRatePPW = getEpcRatePPWLabel({
    hasArbitrageBattery,
    hasBackupBatteryKWH,
  });

  const {
    epcTotalPaymentDomesticContentElements,
    epcRatePPWDomesticContentElements,
    totalEPCPaymentElements,
    effectivePPWElements,
  } = useGetDomesticElements({
    quote: item,
    totalSystemCost,
    totalAdderCost,
    pricePerWatt,
  });

  return (
    <Details isOpen={isDetailsOpen} gap="sm" borderWidth="xs 0 0 0" borderColor="separator">
      <Details.Summary
        isDetailsOpen={isDetailsOpen}
        onToggle={showVoidButton ? undefined : handleDetailsToggle}
        display="inline"
        cursor={'pointer'}
        width="100"
      >
        <Box width="100" gap="5xl-tablet 5xl-desktop" columnGap={'md'} direction="row" padding={'sm lg'}>
          <Box width="lg" margin={indentLeft ? '0 0 0 2xl' : undefined}>
            <Badge
              message={
                quoteType === ConsumerSolarQuoteType.initial && status === QuoteStatus.active // this prop should only appear for consumer solar quotes
                  ? 'Initial Quote'
                  : quoteStatus[status as keyof typeof QuoteStatus]?.label
              }
              variant={quoteStatus[status as keyof typeof QuoteStatus]?.variant} //don't have to do anything special here for Initial Consumer Solar quotes
              style={{ width: 'fit-content' }}
            />
          </Box>
          <Box gap="xs" flex="auto">
            <Box>{title || '---'}</Box>
            <Box fontSize="xs" color="body-secondary">
              {getAdderList(adders)}
            </Box>
          </Box>
          <Box display={'flex'} direction="row" childGap="xs">
            {showCreateChangeOrderButton && [QuoteStatus.active, QuoteStatus.contracted].includes(status) && (
              <Box style={{ whiteSpace: 'nowrap' }}>
                <RequirePermissions
                  permissions={[UserPermissions.admin, UserPermissions.editor]}
                  checkAllPermissions={false}
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    tone="neutral"
                    isDisabled={isVoidingQuote || isSendingContract || !isAccountEditEnabled || !allowsAction}
                    onClick={() => navigate(`/accounts/${item.accountId}/change-order/create`)}
                  >
                    Change Order
                  </Button>
                </RequirePermissions>
              </Box>
            )}
            {showVoidButton ? (
              <Box>
                <RequirePermissions
                  permissions={[UserPermissions.admin, UserPermissions.editor]}
                  checkAllPermissions={false}
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    tone="danger"
                    disabled={isVoidingQuote || isSendingContract || !isAccountEditEnabled || !allowsAction}
                    onClick={handleVoidQuote}
                  >
                    Void
                  </Button>
                </RequirePermissions>
              </Box>
            ) : (
              <Box fontSize="xs">{isDetailsOpen ? 'Hide Details' : 'Show Details'}</Box>
            )}
          </Box>
        </Box>
      </Details.Summary>

      <Box padding="0 0 lg 0">
        <CustomerFacingSection
          item={item}
          isHVACWaterHeaterQuote={isHVACWaterHeaterQuote}
          hasArbitrageBattery={hasArbitrageBattery}
          hasBackupBatteryKWH={hasBackupBatteryKWH}
          hasCostBasedBattery={hasCostBasedBattery}
          systemPricingDetails={systemPricingDetails}
          hasSolarReadiness={hasSolarReadiness}
          solarReadinessCost={solarReadinessCost}
          totalAmountPaid={totalAmountPaid}
          valueStyle={struckStyle}
        />

        <EPCFacingSection
          isHVACWaterHeaterQuote={isHVACWaterHeaterQuote}
          epcRatePPW={epcRatePPW}
          epcTotalPaymentLabel={epcTotalPaymentLabel}
          epcRatePPWDomesticContentElements={epcRatePPWDomesticContentElements}
          epcTotalPaymentDomesticContentElements={epcTotalPaymentDomesticContentElements}
          totalSystemCost={totalSystemCost}
          hasCostBasedBattery={hasCostBasedBattery}
          totalBatteryCost={totalBatteryCost}
          totalEPCPaymentElements={totalEPCPaymentElements}
          pricePerWatt={pricePerWatt}
          totalAdderCost={totalAdderCost}
          effectivePPWElements={effectivePPWElements}
          effectivePPWRate={item.effectivePPWRate}
          valueStyle={struckStyle}
        />
      </Box>
    </Details>
  );
}
